import { IGif, IUser } from "@/common/services/types";
import { UserInfo } from "../UserInfo";
import { TagList } from "components/tags";
import { Button } from "@/components/buttons/Button";
import { memo, useCallback, useMemo } from "react";
import LiveButton from "@/components/ads/LiveButton/LiveButton";
import { clsx } from "clsx";
import "./MetaInfo.scss";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import { useDismissTagFeedbackTooltip } from "components/gifPreview/GifPreview/hooks/useDismissTagFeedbackTooltip";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { EventSource, metricsBus } from "@/common/buses";
import { useUserSubscription } from "@/common/hooks/useUserSubscription";
import { useNichesStore } from "@/common/store/storeNiches";
import { updateCtaLink } from "@/common/utils/boostedGifUtils";

interface IMetaInfoProps {
  gif: IGif;
  tags: string[];
  user: IUser | undefined;
  rel?: string;
  currentUser: CurrentUserObject | null;
  tagFeedbackMode: boolean;
  irrelevantTags: string[];
  tagFeedbackLoading: boolean;
  onTagFeedbackMode: (enabled: boolean) => void;
  onSendTagFeedback: (tag: string) => void;
  isVisible: boolean;
  position: number;
  playCount: number;
}

export const MetaInfo = memo(({
  gif,
  tags,
  user,
  rel,
  currentUser,
  tagFeedbackMode,
  irrelevantTags,
  tagFeedbackLoading,
  onTagFeedbackMode,
  onSendTagFeedback,
  isVisible,
  position,
  playCount
}: IMetaInfoProps) => {
  const { isShowTagFeedbackTooltip, onDismissTagFeedbackTooltip, } = useDismissTagFeedbackTooltip({
    position,
    isVisible,
    gifId: gif.id,
    currentUser
  });

  const eventSource = useAnalyticsStore((state) => state.eventSource);
  const niches = useNichesStore(state => state.niches);

  const { subscribed, subscribe, unsubscribe } = useUserSubscription(gif.userName, true);

  const onSubscribe = useCallback(() => {
    subscribed ? unsubscribe() : subscribe({ gifId: gif.id, position, playCount });
  }, [gif, position, playCount, subscribed]);

  const onProfileMetric = useCallback((value: string) => {
    metricsBus.emit("profileClicked", {
      gifId: gif.id,
      creator: value,
      source: eventSource?.name ?? EventSource.Other,
      sourceId: eventSource?.id ?? null,
      position,
      playCount,
      userName: currentUser?.username,
    });

    if (gif.cta?.campaignId) {
      metricsBus.emit("boostProfileClicked", {
        campaignId: gif.cta.campaignId
      });
    }
  }, [eventSource, position, playCount, gif]);

  const currentTags = useMemo(() => {
    return tags.map((tag: string) => ({
      name: tag,
      count: 0,
    }));
  }, [tags]);

  const gifNiches = useMemo(() => {
    if (!gif.niches) {
      return [];
    }

    return gif.niches.map(id => niches[id]).filter(Boolean);
  }, [gif.niches, niches]);

  const hasCta = gif.cta && gif.cta.text && gif.cta.link;
  const ctaUrl = useMemo(() => updateCtaLink(gif.cta?.link ?? null, position, gif.cta?.showReason ?? null), [gif.cta]);

  return (
    <div className={clsx("MetaInfo", hasCta && "MetaInfo_isBoosted")}>
      {user && <UserInfo
        isFollowing={subscribed}
        onFollow={onSubscribe}
        currentUser={currentUser}
        gif={gif}
        user={user}
        rel={rel}
        onProfileMetric={onProfileMetric}
      />}
      {!hasCta && (currentTags.length + gifNiches.length) > 0 && (
        <TagList
          niches={gifNiches}
          tags={currentTags}
          irrelevantTags={irrelevantTags}
          tagFeedbackLoading={tagFeedbackLoading}
          isShowTagFeedbackTooltip={isShowTagFeedbackTooltip}
          onDismissTagFeedbackTooltip={onDismissTagFeedbackTooltip}
          onTagFeedbackMode={onTagFeedbackMode}
          onSendTagFeedback={onSendTagFeedback}
          tagFeedbackMode={tagFeedbackMode}
          withFeedback={!!currentUser?.name}
        />
      )}
      {hasCta && ctaUrl && (
        <div className="MetaInfo-BoostedBtn">
          <Button
            aria-label={gif.cta?.text}
            fullWidth
            onClick={() => window.open(ctaUrl, "_blank", "noopener,noreferrer")}
          >{gif.cta?.text}</Button>
        </div>
      )}
      <div className="MetaInfo-LiveButton">
        <LiveButton />
      </div>
    </div>
  );
});
